import React, {Component} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Menu from '@material-ui/core/Menu';
import MenuItemMUI from '@material-ui/core/MenuItem';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MuiButton from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import UndoIcon from '@material-ui/icons/Undo';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Drawer from '@material-ui/core/Drawer';
import Preloader from '../../components/preloader';
import EmptyState from '../../components/empty-state-container';
import Button from '../../components/button';
import ErrorBoundary from '../../components/error-boundary';
import ChangeRequestDetailSheet, {normalizeFieldValue as normalizeDiffValue} from '../../components/change-request-detail-sheet';
import FieldVersionHistorySheet from '../../components/field-version-history-sheet';
import RedactedChip from '../../components/redacted-chip';
import {sanitizeChangeControl} from '../../util/change-control-text';
import {
  loadChanges,
  applyAction,
  rollbackAction,
  recheckSource,
  loadChangeTimeline,
  bulkAction,
  loadHistory,
  loadPermissions,
  downloadAuditReport,
  downloadDetailedReport,
  downloadSummaryReport,
  loadFieldTimeline,
  loadChangeRequestById,
} from './workflow-dashboard.actions';
import Auth from '../../auth/auth';
import {sourceCheckMeta} from '../../util/source-check';

const auth = new Auth();

const STATUS_CHIP_CLASSES = {
  PENDING: 'chip-orange',
  APPROVED: 'chip-green',
  REJECTED: 'chip-red',
  IMPLEMENTED: 'chip-blue',
  ROLLED_BACK: 'chip-orange',
};

const CHANGE_TYPE_CHIP_CLASSES = {
  CREATE: 'chip-green',
  UPDATE: 'chip-orange',
  DELETE: 'chip-red',
};

// Normalize boolean-like field values (kept in sync with dd-change-detail-dialog.js)
function normalizeFieldValue(field, value) {
  if (field === 'isNullable') {
    if (value === '1' || value === 'YES' || value === true || value === 1) return 'YES';
    if (value === '0' || value === 'NO' || value === false || value === 0) return 'NO';
  }
  return value;
}

// Distinct schemas in the loaded change set, keyed by schema_id (globally unique)
// and labelled by schema_name (only unique per instance). Rows with no schema_id
// are skipped. Used to build the client-side Schema filter options.
export function distinctSchemas(changes) {
  const byId = new Map();
  (changes || []).forEach((c) => {
    if (c.schema_id && !byId.has(c.schema_id)) {
      byId.set(c.schema_id, {id: c.schema_id, name: c.schema_name || c.schema_id, instance: c.instance_name || ''});
    }
  });
  const list = [...byId.values()];
  // When two Data Dictionaries share a schema_name, the plain name is ambiguous in the
  // filter — qualify those with the DD (instance) name so the two options are distinct (#595).
  const nameCounts = {};
  list.forEach((s) => {
    nameCounts[s.name] = (nameCounts[s.name] || 0) + 1;
  });
  list.forEach((s) => {
    s.label = (nameCounts[s.name] > 1 && s.instance) ? `${s.name} · ${s.instance}` : s.name;
  });
  return list.sort((a, b) => a.label.localeCompare(b.label));
}

export function filterChangesBySchema(changes, schemaId) {
  return schemaId ? (changes || []).filter((c) => c.schema_id === schemaId) : (changes || []);
}

export function historyDistinctValues(history, field) {
  const seen = new Set();
  (history || []).forEach((e) => {
    if (e[field]) seen.add(e[field]);
  });
  return [...seen].sort((a, b) => String(a).localeCompare(String(b)));
}

export function filterHistory(history, filters) {
  const {search, schema, object, changeType, from, to} = filters || {};
  const q = (search || '').trim().toLowerCase();
  return (history || []).filter((e) => {
    if (q) {
      const hay = [e.schema_name, e.object_name, e.column_name, e.changed_by, e.changed_by_display]
          .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    // Key on schema_id (globally unique) not schema_name (unique only per instance),
    // mirroring distinctSchemas — history can span instances with same-named schemas.
    if (schema && e.schema_id !== schema) return false;
    if (object && e.object_name !== object) return false;
    if (changeType && e.change_type !== changeType) return false;
    const day = (e.changed_at || '').slice(0, 10);
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
  });
}

export function groupByDayBucket(entries, now = new Date()) {
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const groups = [];
  let last = null;
  (entries || []).forEach((e) => {
    const d = new Date(e.changed_at);
    let label;
    if (!e.changed_at || isNaN(d.getTime())) {
      label = 'Unknown date';
    } else {
      const diff = Math.round((startOf(now) - startOf(d)) / 864e5);
      if (diff <= 0) label = 'Today';
      else if (diff === 1) label = 'Yesterday';
      else if (diff < 7) label = 'Earlier this week';
      else if (diff < 31) label = 'Earlier this month';
      else label = d.toLocaleString(undefined, {month: 'long', year: 'numeric'});
    }
    if (label !== last) {
      groups.push({label, entries: []}); last = label;
    }
    groups[groups.length - 1].entries.push(e);
  });
  return groups;
}

export function relativeTime(dateStr, now = new Date()) {
  const ms = now - new Date(dateStr);
  const min = ms / 6e4; const h = min / 60; const day = h / 24;
  if (!isFinite(min)) return '';
  if (min < 60) return `${Math.max(1, Math.round(min))}m ago`;
  if (h < 24) return `${Math.round(h)}h ago`;
  if (day < 8) return `${Math.round(day)}d ago`;
  return new Date(dateStr).toLocaleString(undefined, {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});
}

export function avatarInitials(name) {
  if (!name) return '·';
  const s = String(name);
  if (/^\d+$/.test(s.trim())) return '·';
  const parts = s.replace(/[._]/g, ' ').split(' ').filter(Boolean);
  return ((parts[0] || '')[0] || '?').toUpperCase() + ((parts[1] || '')[0] || '').toUpperCase();
}

const OBJECT_ATTR_KEYS = [
  ['object_name', 'objectName'], ['description', 'description'],
  ['business_context', 'businessContext'], ['business_owner', 'businessOwner'],
  ['tech_owner', 'techOwner'], ['row_count', 'rowCount'], ['size_mb', 'sizeMb'], ['owner', 'owner'],
];

function objectAttrsChanged(prev, next) {
  const norm = (x) => (x !== null && typeof x === 'object') ? JSON.stringify(x) : String(x ?? '');
  for (const [snake, camel] of OBJECT_ATTR_KEYS) {
    const nv = next[camel] !== undefined ? next[camel] : next[snake];
    if (nv === undefined) continue;
    const ov = prev[snake] !== undefined ? prev[snake] : prev[camel];
    if (norm(nv) !== norm(ov)) return true;
  }
  return false;
}

// A container object row is written on a column-only object approval and is misleading
// ("the object itself didn't change"). Hide it — but KEEP genuine/mixed object changes.
export function isContainerObjectVersion(entry) {
  if (!entry) return false;
  if ((entry.entity_type || '').toUpperCase() !== 'OBJECT') return false;
  if ((entry.change_type || '').toUpperCase() !== 'UPDATE') return false;
  let nv = entry.new_value;
  let pv = entry.previous_value;
  if (typeof nv === 'string') {
    try {
      nv = JSON.parse(nv);
    } catch (e) {
      return false;
    }
  }
  if (typeof pv === 'string') {
    try {
      pv = JSON.parse(pv);
    } catch (e) {
      pv = {};
    }
  }
  if (!nv || !Array.isArray(nv.columnDiffs) || nv.columnDiffs.length === 0) return false;
  return !objectAttrsChanged(pv || {}, nv);
}

// The real per-field change for a version row, derived from previous_value (the old
// dd_column_registry row, snake_case) vs new_value (the engine snapshot, camelCase).
// Limited to fields the registry actually stores AND the snapshot carries — snapshot-only
// fields (columnKey/isUnsigned/…) would false-positive against a missing registry side,
// and registry-only PK/FK map to the snapshot's columnKey (no clean pair). Rarer changes
// (PK/index/unsigned) fall back to the description. [registrySnake, snapshotCamel].
const VERSION_DIFF_FIELDS = [
  ['column_name', 'columnName'],
  ['data_type', 'dataType'],
  ['max_length', 'maxLength'],
  ['is_nullable', 'isNullable'],
  ['default_value', 'defaultValue'],
  ['is_unique', 'isUnique'],
];

export function versionFieldDiffs(previousValue, newValue) {
  const parse = (v) => {
    if (v == null) return {}; if (typeof v === 'object') return v; try {
      return JSON.parse(v);
    } catch (e) {
      return {};
    }
  };
  const pv = parse(previousValue);
  const nv = parse(newValue);
  const cmp = (x) => (x == null ? '' : String(x));
  const disp = (x) => (x == null || x === '' ? '—' : String(x));
  const out = [];
  for (const [snake, camel] of VERSION_DIFF_FIELDS) {
    if (nv[camel] === undefined && nv[snake] === undefined) continue; // new side must carry the field
    const oldN = normalizeDiffValue(camel, pv[snake] !== undefined ? pv[snake] : pv[camel]);
    const newN = normalizeDiffValue(camel, nv[camel] !== undefined ? nv[camel] : nv[snake]);
    if (cmp(oldN) !== cmp(newN)) out.push({field: camel, oldValue: disp(oldN), newValue: disp(newN)});
  }
  return out;
}

export class WorkflowDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      expandedGroups: {},
      filterInstance: '',
      filterSchema: '',
      filterStatus: '',
      filterSource: '',
      filterFrom: '',
      filterTo: '',
      changeDialogOpen: false,
      selectedChangeRequest: null,
      historyExpanded: false,
      historyFilters: {search: '', schema: '', object: '', changeType: '', from: '', to: ''},
      reportMenuAnchorEl: null,
      snackbarOpen: false,
      snackbarMessage: '',
      rollbackDialogOpen: false,
      rollbackTargetRequest: null,
      rollbackReason: '',
      rollbackReasonError: false,
      groupMenuAnchor: null,
      groupMenuItems: null,
      rowMenuAnchor: null,
      rowMenuRow: null,
      bulkActionsAnchor: null,
      filterDrawerOpen: false,
      recheckingId: null,
      timeline: [],
      fieldTimelineOpen: false,
      fieldTimelineFor: null,
      fieldTimeline: [],
      fieldTimelineLoading: false,
    };
  }

  handleRecheckSource = (requestId) => {
    this.setState({recheckingId: requestId});
    this.props.recheckSource(requestId)
        .then((json) => {
          const outcome = json && json.response && json.response.outcome;
          const msg =
          outcome === 'IMPLEMENTED' ? 'Reverted at source — closed (Implemented).' :
          outcome === 'RE_DECIDE' ? 'Source value changed — reopened for re-review.' :
          outcome === 'SOURCE_UNREACHABLE' ? 'Source DB unreachable — inconclusive.' :
          (outcome === 'SKIPPED' || outcome === 'NOT_FOUND') ? 'This change can\'t be verified against the source (not a checkable column change).' :
          'Still present in the source DB — the DB team has not reverted it yet.';
          this.setState({recheckingId: null, snackbarOpen: true, snackbarMessage: msg});
          this.loadData();
          this.reloadHistoryIfOpen();
        })
        .catch(() => this.setState({recheckingId: null, snackbarOpen: true, snackbarMessage: 'Live recheck failed — please try again.'}));
  };

  componentDidMount() {
    this.loadData();
    this.props.loadPermissions();
    this._handleDeepLink();
  }

  _handleDeepLink() {
    const search = this.props.location && this.props.location.search;
    if (!search) return;
    const params = new URLSearchParams(search);
    const requestId = params.get('request');
    const schemaId = params.get('schema');
    if (requestId) {
      this.props.loadChangeRequestById(requestId)
          .then((cr) => {
            if (cr) {
              this.setState({changeDialogOpen: true, selectedChangeRequest: cr});
              this.props.loadChangeTimeline(requestId)
                  .then((json) => {
                    const events = json && json.response && json.response.events;
                    this.setState({timeline: events || []});
                  })
                  .catch(() => {});
            }
          })
          .catch(() => {});
    } else if (schemaId) {
      this.setState({filterSchema: schemaId});
    }
  }

  // Reload the list when a mutating action (approve/reject/bulk/rollback)
  // finishes. isSubmittingAction flips false only when the POST resolves — i.e. after
  // the backend is fully done — so this never races the write. A source-checkable
  // approve runs a live-DB check + DD apply that routinely exceeded the old fixed 1s
  // timer, leaving the row's status stale until a manual refresh.
  componentDidUpdate(prevProps) {
    if (prevProps.isSubmittingAction && !this.props.isSubmittingAction) {
      this.loadData();
      this.reloadHistoryIfOpen();
    }
  }

  loadData = () => {
    // Schema AND Status are filtered client-side (see getEffectiveSchemaFilter /
    // getVisibleChanges), so neither is sent here. Status is client-side so the stat
    // tiles keep counting the full active set — clicking a status tile filters only the
    // table, never the tile numbers.
    // view=active excludes terminal IMPLEMENTED rows (#595): once closed there is no
    // more action, so they leave the active table and live only in Version History.
    // ponytail: limit 200, no pager — the tiles and the client-side status filter both
    // operate on that newest-200 window; add real pagination if an instance exceeds it.
    const {filterInstance, filterSource} = this.state;
    this.props.loadChanges({
      instanceId: filterInstance || undefined,
      changeSource: filterSource || undefined,
      view: 'active',
      limit: 200,
    });
  };

  // Keep the Version History panel live: when an action closes a change (approve→applied,
  // or reject→reverted), the row leaves the active table AND the new version record must
  // appear without a page refresh. Only reload when the panel is open (else it loads on expand).
  reloadHistoryIfOpen = () => {
    if (this.state.historyExpanded) this.props.loadHistory();
  };

  // Schema filter is client-side; clearing selection stops bulk actions from
  // acting on rows the filter now hides (and fixes the "Actions (n)" count).
  handleSchemaFilterChange = (schemaId) => {
    this.setState({filterSchema: schemaId, selectedItems: []});
  };

  // Guard the stored schema id against the current option set. Any server reload
  // (source/tab change, or a post-action refetch) can drop the selected schema from
  // the loaded set; this self-heals to '' so the Select never goes out-of-range and
  // rows are never hidden behind an invisible filter.
  getEffectiveSchemaFilter = () => {
    const present = distinctSchemas(this.props.changes).some((s) => s.id === this.state.filterSchema);
    return present ? this.state.filterSchema : '';
  };

  // Rows shown in the table: the schema filter (client-side) then the status filter
  // (client-side). Status is applied HERE, not in loadData, so the stat tiles keep
  // counting the full active set while the table narrows to the clicked status.
  getVisibleChanges = () => {
    const schemaVisible = filterChangesBySchema(this.props.changes, this.getEffectiveSchemaFilter());
    const {filterStatus} = this.state;
    return filterStatus ? schemaVisible.filter((c) => c.status === filterStatus) : schemaVisible;
  };

  handleFilterChange = (field, value) => {
    this.setState({[field]: value}, this.loadData);
  };

  // Status is filtered client-side, so a tile click only updates local state — no
  // server refetch. Clear the selection so bulk actions can't act on rows the filter
  // now hides (mirrors handleSchemaFilterChange).
  handleStatCardClick = (status) => {
    const current = this.state.filterStatus;
    const next = (status === 'total' || current === status) ? '' : status;
    this.setState({filterStatus: next, selectedItems: []});
  };

  // The Status dropdowns (filter bar + drawer) share the tile filter: client-side,
  // clears selection, no reload.
  handleStatusFilterChange = (value) => {
    this.setState({filterStatus: value, selectedItems: []});
  };

  handleSelectAllItems = (e) => {
    if (e.target.checked) {
      const visible = this.getVisibleChanges();
      this.setState({selectedItems: visible.map((r) => r.request_id)});
    } else {
      this.setState({selectedItems: []});
    }
  };

  toggleItemSelection = (itemId) => {
    this.setState((prev) => {
      const idx = prev.selectedItems.indexOf(itemId);
      if (idx >= 0) {
        return {selectedItems: prev.selectedItems.filter((id) => id !== itemId)};
      }
      return {selectedItems: [...prev.selectedItems, itemId]};
    });
  };

  toggleGroupSelection = (groupItems) => {
    const groupIds = groupItems.map((i) => i.request_id);
    this.setState((prev) => {
      const allSelected = groupIds.every((id) => prev.selectedItems.includes(id));
      if (allSelected) {
        return {selectedItems: prev.selectedItems.filter((id) => !groupIds.includes(id))};
      }
      const next = [...prev.selectedItems];
      groupIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return {selectedItems: next};
    });
  };

  toggleGroup = (groupKey) => {
    this.setState((prev) => ({
      expandedGroups: {
        ...prev.expandedGroups,
        [groupKey]: !prev.expandedGroups[groupKey],
      },
    }));
  };

  handleHistoryFilterChange = (field, value) => {
    this.setState((prev) => {
      const next = {...prev.historyFilters, [field]: value};
      if (field === 'schema') {
        const scoped = new Set(
            filterHistory(this.props.history, {schema: value}).map((e) => e.object_name).filter(Boolean),
        );
        if (next.object && !scoped.has(next.object)) next.object = '';
      }
      return {historyFilters: next};
    });
  };

  clearHistoryFilters = () => {
    this.setState({historyFilters: {search: '', schema: '', object: '', changeType: '', from: '', to: ''}});
  };

  // Group key includes the Data Dictionary (instance_id) + schema_id so two DDs that
  // share a schema_name don't merge same-named objects into one apparent-duplicate group.
  // ponytail: keys on instance_id + schema_id; a residual edge (legacy SCHEMA_MONITOR rows
  // for the same object with inconsistent schema_id null-ness) could split — fine until seen.
  groupKeyFor = (item) =>
    `${item.instance_id || 'none'}::${item.schema_id || 'none'}::${item.object_name || '(unknown)'}`;

  groupItemsByObject = (items) => {
    const groups = {};
    items.forEach((item) => {
      const key = this.groupKeyFor(item);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  };

  sortGroups = (groups) => {
    // Status-INDEPENDENT order: approving/rejecting a field must not re-sort the list
    // out from under the reviewer. requested_at + name don't change on a status update
    // (only the old status-priority did), so the row stays put. Newest drift first,
    // then object / Data Dictionary / schema. Pending-focus is served by the tiles + filter.
    const entries = Object.entries(groups).map(([key, items]) => ({
      key,
      items,
      objectName: (items[0] && items[0].object_name) || '',
      instanceName: (items[0] && items[0].instance_name) || '',
      schemaName: (items[0] && items[0].schema_name) || '',
      latestTs: Math.max(...items.map((i) => (i.requested_at ? new Date(i.requested_at).getTime() : 0))),
    }));
    entries.sort((a, b) => {
      if (b.latestTs !== a.latestTs) return b.latestTs - a.latestTs;
      return a.objectName.localeCompare(b.objectName) ||
        a.instanceName.localeCompare(b.instanceName) ||
        a.schemaName.localeCompare(b.schemaName);
    });
    return entries.map(({key, items}) => [key, items]);
  };

  sortChildRows = (items) => {
    // Status-independent too, so a field doesn't jump within its group when its
    // status changes. Deterministic by column name, then request id.
    return [...items].sort((a, b) =>
      (a.column_name || '').localeCompare(b.column_name || '') ||
      (a.request_id || '').localeCompare(b.request_id || ''),
    );
  };

  getGroupStatus = (groupItems) => {
    const statuses = [...new Set(groupItems.map((i) => i.status))];
    return statuses.length === 1 ? statuses[0] : 'MIXED';
  };

  renderChildRowChanges = (row) => {
    let changes = null;
    try {
      changes = typeof row.proposed_changes === 'object' ?
        row.proposed_changes :
        JSON.parse(row.proposed_changes || '{}');
    } catch (e) {
      changes = null;
    }

    const columnName = row.column_name;
    if (changes && changes.columnDiffs && changes.columnDiffs.length > 0) {
      // Match by column name; fall back to the only entry if there is exactly one
      const cd = changes.columnDiffs.find((c) => c.columnName === columnName) ||
        (changes.columnDiffs.length === 1 ? changes.columnDiffs[0] : null);
      if (cd) {
        if (cd.type === 'COLUMN_ADDED') {
          return <span>column added</span>;
        }
        if (cd.type === 'COLUMN_REMOVED') {
          return <span>column removed</span>;
        }
        if (cd.type === 'COLUMN_MODIFIED') {
          return (
            <div className="wf-child-change-stack">
              {(cd.fieldDiffs || []).map((fd, i) => (
                <div key={i} className="wf-child-change-stack-item">
                  <span className="wf-change-text">{fd.field} changed</span>
                  <span className="wf-child-change-values">
                    {normalizeFieldValue(fd.field, fd.oldValue) ?? '—'}
                    <span className="arrow">→</span>
                    {normalizeFieldValue(fd.field, fd.newValue) ?? '—'}
                  </span>
                </div>
              ))}
            </div>
          );
        }
      }
    }

    // diff_summary is TEXT, so a withheld one arrives as the literal '[redacted]'. Rendering it
    // raw prints the sentinel as if it were the summary — the thing
    // change-request-detail-sheet.classification.test.js already asserts must not happen in the
    // drawer. The dashboard was the one surface that never got the chip, and /workflow enforcement
    // is what makes it reachable.
    if ((row.redactedFields || []).includes('diff_summary')) {
      return <RedactedChip fields={row.redactedFields} data-testid="wf-diff-redacted" />;
    }
    if (row.diff_summary) {
      return <span className="wf-diff-summary">{row.diff_summary}</span>;
    }
    return <span className="wf-empty-value">—</span>;
  };

  handleBulkAction = (action) => {
    const {selectedItems} = this.state;
    const {changes} = this.props;
    if (selectedItems.length === 0) return;

    // Filter items based on which action can be applied to them
    const selectedSet = new Set(selectedItems);
    const selectedChanges = changes.filter((c) => selectedSet.has(c.request_id));

    let applicableItems = [];

    if (['APPROVED', 'REJECTED'].includes(action)) {
      // These actions can only be applied to PENDING items
      applicableItems = selectedChanges
          .filter((c) => c.status === 'PENDING')
          .map((c) => c.request_id);
    } else if (action === 'ROLLED_BACK') {
      // Rollback can only be applied to open decisions: APPROVED, REJECTED
      // (IMPLEMENTED is terminal and no longer listed in the active table).
      applicableItems = selectedChanges
          .filter((c) => ['APPROVED', 'REJECTED'].includes(c.status))
          .map((c) => c.request_id);
    }

    if (applicableItems.length === 0) {
      this.setState({snackbarOpen: true, snackbarMessage: `No items eligible for ${action}.`});
      return;
    }

    this.setState({selectedItems: []});
    this.reportBulk(this.props.bulkAction(applicableItems, action, ''), action, applicableItems.length);
  };

  // WAITS for the outcomes instead of announcing the count it asked for. Since slice 5j each request
  // in a bulk goes through the same live-source-checked path as a single approval, so a bulk can
  // partially land: some implemented, some sent back for re-review, some invisible to this caller.
  // "Bulk APPROVED applied to 12 item(s)", fired before the response, was false whenever any of that
  // happened — and it fired synchronously, so it was not even waiting to find out.
  reportBulk = (promise, action, requested) => {
    const show = (msg) => this.setState({snackbarMessage: msg, snackbarOpen: true});
    if (!promise || typeof promise.then !== 'function') {
      return show(`${action} submitted for ${requested} item(s).`);
    }
    return promise.then((json) => {
      const rows = (json && json.response) || [];
      if (!Array.isArray(rows) || rows.length === 0) {
        return show(`${action} submitted for ${requested} item(s).`);
      }
      const settled = rows.filter((r) => ['IMPLEMENTED', 'REJECTED', 'REJECTED_OPEN', 'REJECTED_CLOSED'].includes(r.outcome));
      const reopened = rows.filter((r) => ['RE_DECIDE', 'NOT_APPLIED', 'UNREACHABLE'].includes(r.outcome)).length;
      const hidden = rows.filter((r) => r.outcome === 'NOT_VISIBLE').length;
      const failed = rows.filter((r) => r.outcome === 'ERROR').length;
      const parts = [`${settled.length} of ${rows.length} ${action.toLowerCase()}`];
      if (reopened) parts.push(`${reopened} need re-review`);
      if (hidden) parts.push(`${hidden} not visible to you`);
      if (failed) parts.push(`${failed} failed`);
      return show(parts.join(' · ') + '.');
    });
  };

  handleGroupAction = (groupItems, action, e) => {
    if (e) e.stopPropagation();
    const pendingIds = groupItems.filter((i) => i.status === 'PENDING').map((i) => i.request_id);
    if (pendingIds.length === 0) return;
    this.reportBulk(this.props.bulkAction(pendingIds, action, ''), action, pendingIds.length);
  };

  handleInlineAction = (row, action, e) => {
    if (e) e.stopPropagation();
    if (action === 'ROLLBACK') {
      this.openRollbackDialog(row, e);
      return;
    }
    this.props.applyAction(row.request_id, action, '', null);
    this.setState({snackbarOpen: true, snackbarMessage: `${action} submitted.`});
  };

  // Load the full change-history timeline (drift + decisions + live checks) for the
  // drawer (#595). Shared by row-click and prev/next navigation so the drawer never
  // shows a stale timeline for a different field.
  loadTimelineFor = (requestId) => {
    this.setState({timeline: []});
    this.props.loadChangeTimeline(requestId)
    // Stale-response guard: on rapid prev/next a slow earlier response must not
    // overwrite a newer selection's timeline (mirrors openEntityHistory).
        .then((json) => this.setState((prev) =>
        (prev.selectedChangeRequest && prev.selectedChangeRequest.request_id === requestId) ?
          {timeline: (json && json.response) || []} : prev))
        .catch(() => this.setState({timeline: []}));
  };

  openEntityHistory = (entry) => {
    this.setState({fieldTimelineOpen: true, fieldTimelineFor: entry, fieldTimeline: [], fieldTimelineLoading: true});
    // Fall back to the proposed column name when the registry join left column_name null
    // (e.g. an entity_id='—' add row) so the CR lifecycle events still resolve.
    let columnName = entry.column_name;
    if (!columnName) {
      try {
        const nv = typeof entry.new_value === 'object' ? entry.new_value : JSON.parse(entry.new_value || '{}');
        columnName = (nv && (nv.columnName || nv.column_name)) || null;
      } catch (e) {/* leave columnName null */}
    }
    this.props.loadFieldTimeline(entry.schema_id, entry.object_name, columnName, entry.entity_id)
        .then((json) => {
          const rows = (json && json.response) || [];
          // Stale-response guard: a slow earlier fetch must not overwrite a newer field selection.
          this.setState((prev) => (prev.fieldTimelineFor && prev.fieldTimelineFor.entity_id === entry.entity_id) ?
          {fieldTimeline: rows, fieldTimelineLoading: false} : prev);
        })
        .catch(() => this.setState((prev) => (prev.fieldTimelineFor && prev.fieldTimelineFor.entity_id === entry.entity_id) ?
        {fieldTimelineLoading: false} : prev));
  };

  handleRowClick = (changeRequest) => {
    this.setState({changeDialogOpen: true, selectedChangeRequest: changeRequest});
    this.loadTimelineFor(changeRequest.request_id);
  };

  handleNavigateChangeRequest = (direction) => {
    const {selectedChangeRequest, expandedGroups} = this.state;
    // Navigate within the visible (schema + status filtered) set so prev/next never
    // lands on a hidden row and matches the sheet's disabled prev/next state.
    const changes = this.getVisibleChanges();

    if (!selectedChangeRequest || !changes || changes.length === 0) return;

    // Build a flattened list of changes in the visual order they appear in the table
    const groupedChanges = this.groupItemsByObject(changes);
    const sortedGroups = this.sortGroups(groupedChanges);
    const visibleChanges = [];

    sortedGroups.forEach(([objectName, groupItems]) => {
      const sortedChildren = this.sortChildRows(groupItems);
      // Include all rows so navigation is consistent
      visibleChanges.push(...sortedChildren);
    });

    if (visibleChanges.length === 0) return;

    // Find current index in the visual order
    const currentIndex = visibleChanges.findIndex((c) =>
      c.request_id === selectedChangeRequest.request_id,
    );

    if (currentIndex === -1) return;

    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : visibleChanges.length - 1;
    } else {
      newIndex = currentIndex < visibleChanges.length - 1 ? currentIndex + 1 : 0;
    }

    const newChangeRequest = visibleChanges[newIndex];

    // Ensure the group containing this change request is expanded
    const groupKey = this.groupKeyFor(newChangeRequest);
    const newExpandedGroups = {...expandedGroups, [groupKey]: true};

    this.setState({
      selectedChangeRequest: newChangeRequest,
      expandedGroups: newExpandedGroups,
    });
    // Reload the timeline for the newly-selected field (else the drawer keeps the prior field's history).
    this.loadTimelineFor(newChangeRequest.request_id);
  };

  handleDialogAction = (action, comments) => {
    const {selectedChangeRequest} = this.state;
    if (!selectedChangeRequest) return;
    this.props.applyAction(selectedChangeRequest.request_id, action, comments);
    this.setState({changeDialogOpen: false, selectedChangeRequest: null, snackbarOpen: true, snackbarMessage: `${action} submitted successfully.`});
  };

  handleDownloadReport = (type) => {
    const {filterInstance, filterFrom, filterTo} = this.state;
    const filters = {instanceId: filterInstance, from: filterFrom, to: filterTo};
    if (type === 'audit') this.props.downloadAuditReport(filters);
    else if (type === 'detailed') this.props.downloadDetailedReport(filters);
    else if (type === 'summary') this.props.downloadSummaryReport(filters);
    this.setState({reportMenuAnchorEl: null});
  };

  openRollbackDialog = (changeRequest, e) => {
    if (e) e.stopPropagation();
    this.setState({rollbackDialogOpen: true, rollbackTargetRequest: changeRequest, rollbackReason: '', rollbackReasonError: false});
  };

  closeRollbackDialog = () => {
    this.setState({rollbackDialogOpen: false, rollbackTargetRequest: null, rollbackReason: '', rollbackReasonError: false});
  };

  confirmRollback = () => {
    const {rollbackTargetRequest, rollbackReason} = this.state;
    if (!rollbackReason || !rollbackReason.trim()) {
      this.setState({rollbackReasonError: true});
      return;
    }
    this.props.rollbackAction(rollbackTargetRequest.request_id, rollbackReason.trim());
    this.setState({
      rollbackDialogOpen: false,
      rollbackTargetRequest: null,
      rollbackReason: '',
      snackbarOpen: true,
      snackbarMessage: 'Rollback submitted successfully.',
    });
  };

  computeSummary = (list) => {
    // IMPLEMENTED is terminal and excluded from the active table (#595), so it is not
    // counted here — the tiles summarise active work only.
    const changes = list || this.props.changes;
    const counts = {PENDING: 0, APPROVED: 0, REJECTED: 0};
    changes.forEach((c) => {
      if (counts[c.status] !== undefined) counts[c.status]++;
    });
    counts.total = counts.PENDING + counts.APPROVED + counts.REJECTED;
    return counts;
  };

  // The real change for a feed row — matches the main table's "Changes" column instead
  // of the generic stored description (which is only shown as a last-resort fallback).
  renderVersionChange = (entry) => {
    const et = (entry.entity_type || '').toUpperCase();
    const ct = (entry.change_type || '').toUpperCase();
    if (et === 'COLUMN') {
      if (ct === 'CREATE' || ct === 'INSERT') return <span className="vh-change-text">column added</span>;
      if (ct === 'DELETE') return <span className="vh-change-text">column removed</span>;
    }
    if (et === 'OBJECT') {
      if (ct === 'CREATE') return <span className="vh-change-text">object created</span>;
      if (ct === 'DELETE') return <span className="vh-change-text">object removed</span>;
      let nv = entry.new_value;
      if (typeof nv === 'string') {
        try {
          nv = JSON.parse(nv);
        } catch (e) {
          nv = null;
        }
      }
      if (nv && (nv.definitionChanged || nv.newDefinition || nv.definition)) {
        return <span className="vh-change-text">definition changed</span>;
      }
    }
    const diffs = versionFieldDiffs(entry.previous_value, entry.new_value);
    if (diffs.length > 0) {
      return (
        <span className="vh-change-stack">
          {diffs.map((d, i) => (
            <span key={i} className="vh-change-item">
              <span className="vh-change-text">{d.field}</span>
              <span className="val">{d.oldValue}</span>
              <span className="arrow">→</span>
              <span className="val">{d.newValue}</span>
            </span>
          ))}
        </span>
      );
    }
    return <span className="vh-change-text">{sanitizeChangeControl(entry.change_description) || '—'}</span>;
  };

  renderVersionHistory = () => {
    const {history} = this.props;
    const {historyFilters} = this.state;
    const cleanHistory = (history || []).filter((e) => !isContainerObjectVersion(e));
    const filtered = filterHistory(cleanHistory, historyFilters)
        .slice()
        .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));
    const dayGroups = groupByDayBucket(filtered);

    // Schema options keyed on schema_id (globally unique), labelled by name —
    // reuse distinctSchemas so the feed filter matches the main dashboard's Schema filter.
    const schemaOptions = distinctSchemas(cleanHistory);
    const objectOptions = historyDistinctValues(
        filterHistory(cleanHistory, {schema: historyFilters.schema}),
        'object_name',
    );

    let globalIdx = 0;

    return (
      <div>
        <div className="vh-filters">
          <TextField
            size="small"
            label="Search"
            placeholder="Search field, object, or who changed it…"
            value={historyFilters.search}
            onChange={(e) => this.handleHistoryFilterChange('search', e.target.value)}
            InputLabelProps={{shrink: true}}
            className="vh-search"
            inputProps={{'data-testid': 'cc-vh-search'}}
          />
          <TextField
            select
            size="small"
            label="Schema"
            value={historyFilters.schema}
            onChange={(e) => this.handleHistoryFilterChange('schema', e.target.value)}
            InputLabelProps={{shrink: true}}
            SelectProps={{displayEmpty: true}}
            className="vh-filter-field"
            inputProps={{'data-testid': 'cc-vh-filter-schema'}}
          >
            <MenuItem value="">All</MenuItem>
            {schemaOptions.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Object"
            value={historyFilters.object}
            onChange={(e) => this.handleHistoryFilterChange('object', e.target.value)}
            InputLabelProps={{shrink: true}}
            SelectProps={{displayEmpty: true}}
            className="vh-filter-field"
            inputProps={{'data-testid': 'cc-vh-filter-object'}}
          >
            <MenuItem value="">All</MenuItem>
            {objectOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Change Type"
            value={historyFilters.changeType}
            onChange={(e) => this.handleHistoryFilterChange('changeType', e.target.value)}
            InputLabelProps={{shrink: true}}
            SelectProps={{displayEmpty: true}}
            className="vh-filter-field"
            inputProps={{'data-testid': 'cc-vh-filter-type'}}
          >
            <MenuItem value="">All</MenuItem>
            {['CREATE', 'UPDATE', 'DELETE', 'RESTORE'].map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="From"
            value={historyFilters.from}
            onChange={(e) => this.handleHistoryFilterChange('from', e.target.value)}
            InputLabelProps={{shrink: true}}
            className="vh-filter-field"
            inputProps={{'data-testid': 'cc-vh-filter-from'}}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={historyFilters.to}
            onChange={(e) => this.handleHistoryFilterChange('to', e.target.value)}
            InputLabelProps={{shrink: true}}
            className="vh-filter-field"
            inputProps={{'data-testid': 'cc-vh-filter-to'}}
          />
          <span className="vh-clear" onClick={this.clearHistoryFilters} data-testid="cc-vh-clear">
            Clear all
          </span>
        </div>

        <div className="vh-count">
          <span>{filtered.length} change{filtered.length !== 1 ? 's' : ''}</span>
          {cleanHistory.length >= 200 && <span className="vh-count-cap"> · showing latest 200</span>}
        </div>

        {filtered.length === 0 ? (
          <div className="vh-empty" data-testid="cc-vh-empty">
            No changes match these filters{cleanHistory.length >= 200 ? ' (searching the latest 200 changes)' : ''}.
          </div>
        ) : (
          <div className="vh-feed" data-testid="cc-vh-feed">
            {dayGroups.map((group) => (
              <div key={group.label}>
                <div className="vh-daylabel">{group.label}</div>
                {group.entries.map((entry) => {
                  const idx = globalIdx++;
                  const et = (entry.entity_type || '').toUpperCase();
                  const tc = (entry.change_type || '').toLowerCase();
                  const chipType = tc === 'insert' ? 'create' : tc; // column-add writes INSERT
                  const who = entry.changed_by_display || entry.changed_by || '—';
                  const clickable = et === 'COLUMN' && entry.entity_id && entry.entity_id !== 'new';

                  let columnLabel = entry.column_name;
                  if (!columnLabel && et === 'COLUMN') {
                    let nv = entry.new_value;
                    if (typeof nv === 'string') {
                      try {
                        nv = JSON.parse(nv);
                      } catch (e) {
                        nv = null;
                      }
                    }
                    columnLabel = (nv && (nv.columnName || nv.column_name)) || entry.entity_id;
                  }

                  let pathInner;
                  if (et === 'OBJECT') {
                    pathInner = (
                      <>
                        {entry.schema_name && <span className="vh-schema">{entry.schema_name}</span>}
                        {entry.schema_name && <span className="vh-sep">›</span>}
                        <span className="vh-object">{entry.object_name || entry.entity_id}</span>
                        <span className="vh-sep">›</span>
                        <span className="vh-field-name is-object">(object)</span>
                      </>
                    );
                  } else if (et === 'COLUMN') {
                    pathInner = (
                      <>
                        {entry.schema_name && <span className="vh-schema">{entry.schema_name}</span>}
                        {entry.schema_name && <span className="vh-sep">›</span>}
                        <span className="vh-object">{entry.object_name || '—'}</span>
                        <span className="vh-sep">›</span>
                        <span className="vh-field-name">{columnLabel || '—'}</span>
                      </>
                    );
                  } else if (et === 'SCHEMA') {
                    pathInner = <span className="vh-schema">{entry.schema_name || entry.entity_id}</span>;
                  } else {
                    pathInner = <span className="vh-object">{entry.object_name || entry.entity_id}</span>;
                  }

                  return (
                    <div
                      key={entry.version_id || idx}
                      className="vh-event"
                      data-testid={clickable ? `cc-vh-event-open-${idx}` : `cc-vh-event-${idx}`}
                      onClick={clickable ? () => this.openEntityHistory(entry) : undefined}
                      style={clickable ? {cursor: 'pointer'} : undefined}
                    >
                      <div className="vh-dot-col">
                        <span className={`vh-dot ${chipType}`} />
                      </div>
                      <div className="vh-main">
                        <div className="vh-path">
                          {entry.instance_name && (
                            <span className="vh-instance" data-testid={`cc-vh-instance-${idx}`}>{entry.instance_name}</span>
                          )}
                          {pathInner}
                          <span className={`type-chip ${chipType}`}>{chipType.toUpperCase()}</span>
                          {entry.version_number != null && <span className="vh-ver">v{entry.version_number}</span>}
                        </div>
                        <div className="vh-detail">{this.renderVersionChange(entry)}</div>
                      </div>
                      <div className="vh-meta">
                        <div className="vh-who">
                          <span className="vh-avatar">{avatarInitials(who)}</span>
                          {who}
                        </div>
                        <Tooltip title={entry.changed_at ? new Date(entry.changed_at).toLocaleString() : ''}>
                          <div className="vh-when">{relativeTime(entry.changed_at)}</div>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  render() {
    const {changes, isLoadingChanges, history, isLoadingHistory, isSubmittingAction, permissions} = this.props;
    const {
      selectedItems, expandedGroups, filterStatus, filterSource, historyExpanded, historyFilters,
      changeDialogOpen, selectedChangeRequest, reportMenuAnchorEl,
      snackbarOpen, snackbarMessage,
      rollbackDialogOpen, rollbackTargetRequest, rollbackReason, rollbackReasonError,
      groupMenuAnchor, groupMenuItems, rowMenuAnchor, rowMenuRow,
    } = this.state;

    // Client-side Schema filter: options from the loaded set, selection self-healed
    // against those options so a server refetch can't strand it out-of-range.
    const schemaOptions = distinctSchemas(changes);
    const effectiveFilterSchema = this.getEffectiveSchemaFilter();
    const schemaVisibleChanges = filterChangesBySchema(changes, effectiveFilterSchema);

    // Tiles count the schema-filtered but status-UNfiltered set, so clicking a status
    // tile never changes the tile numbers. The table below uses visibleChanges, which
    // additionally applies the status filter.
    const summary = this.computeSummary(schemaVisibleChanges);
    const visibleChanges = this.getVisibleChanges();

    const isSuperUser = auth.getIsSuperUser();
    const userRoles = auth.getUserRoles ? auth.getUserRoles() : [];
    const userRoleIds = new Set(userRoles.map((r) => r.id || r.role_id));
    const canRollback = isSuperUser || permissions.some(
        (p) => userRoleIds.has(p.role_id) && p.can_rollback,
    );

    const selectedSet = new Set(selectedItems);
    const selectedChanges = visibleChanges.filter((c) => selectedSet.has(c.request_id));
    const anyPendingSelected = selectedChanges.some((c) => c.status === 'PENDING');
    const anyRollbackableSelected = selectedChanges.some((c) => ['APPROVED', 'REJECTED'].includes(c.status));

    // Calculate counts for each action
    const pendingCount = selectedChanges.filter((c) => c.status === 'PENDING').length;
    const rollbackableCount = selectedChanges.filter((c) => ['APPROVED', 'REJECTED'].includes(c.status)).length;

    const groupedMap = this.groupItemsByObject(visibleChanges);
    const sortedGroups = this.sortGroups(groupedMap);

    const statCardDefs = [
      {key: 'total', label: 'TOTAL', cls: 'total'},
      {key: 'PENDING', label: 'PENDING', cls: 'pending'},
      {key: 'APPROVED', label: 'APPROVED', cls: 'approved'},
      {key: 'REJECTED', label: 'REJECTED', cls: 'rejected'},
      // IMPLEMENTED card removed (#595): terminal rows live in Version History, not the table.
    ];

    return (
      <ErrorBoundary>
        <div className="app-content-container">
          <div className="page-content results-container in">

            <div className='cz-container'>

              {/* Page Header */}
              <div className="page-header-row-centered">
                <h2 data-testid="change-control-page-title">Change Control Dashboard</h2>
                <div className="page-header-actions">
                  <Button
                    label="Download Report"
                    className="btn btn-action"
                    onClick={(e) => this.setState({reportMenuAnchorEl: e.currentTarget})}
                  />
                  <Menu
                    anchorEl={reportMenuAnchorEl}
                    open={Boolean(reportMenuAnchorEl)}
                    onClose={() => this.setState({reportMenuAnchorEl: null})}
                    anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    transformOrigin={{horizontal: 'right', vertical: 'top'}}
                  >
                    <MenuItemMUI onClick={() => this.handleDownloadReport('audit')}>Audit Report</MenuItemMUI>
                    <MenuItemMUI onClick={() => this.handleDownloadReport('detailed')}>Detailed Report</MenuItemMUI>
                    <MenuItemMUI onClick={() => this.handleDownloadReport('summary')}>Summary Report</MenuItemMUI>
                  </Menu>
                  <Menu
                    anchorEl={this.state.bulkActionsAnchor}
                    open={Boolean(this.state.bulkActionsAnchor)}
                    onClose={() => this.setState({bulkActionsAnchor: null})}
                    anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    transformOrigin={{horizontal: 'right', vertical: 'top'}}
                  >
                    <MenuItemMUI
                      data-testid="workflow-approve"
                      onClick={() => {
                        this.handleBulkAction('APPROVED'); this.setState({bulkActionsAnchor: null});
                      }}
                      disabled={!anyPendingSelected}
                    >
                      <ListItemIcon style={{minWidth: '36px'}}>
                        <CheckCircleIcon fontSize="small" style={{color: '#4CAF50'}} />
                      </ListItemIcon>
                      Approve {pendingCount > 0 ? `(${pendingCount})` : ''}
                    </MenuItemMUI>
                    <MenuItemMUI
                      data-testid="workflow-reject"
                      onClick={() => {
                        this.handleBulkAction('REJECTED'); this.setState({bulkActionsAnchor: null});
                      }}
                      disabled={!anyPendingSelected}
                    >
                      <ListItemIcon style={{minWidth: '36px'}}>
                        <CancelIcon fontSize="small" style={{color: '#F44336'}} />
                      </ListItemIcon>
                      Reject {pendingCount > 0 ? `(${pendingCount})` : ''}
                    </MenuItemMUI>
                    {/* Bulk rollback removed (#595): rollback is a per-request reopen; the
                        backend /bulk-action route does not accept ROLLED_BACK. */}
                  </Menu>
                </div>
              </div>

              {/* Stat Cards — left-border color-coded, clickable to filter */}
              <div className="workflow-summary-row">
                {statCardDefs.map((card) => (
                  <div key={card.key} className="workflow-stat-tile-wrapper">
                    <Tooltip title={card.key === 'total' ? 'Show all' : `Filter by ${card.label}`} placement="top">
                      <div
                        data-testid={'workflow-card-' + card.key}
                        className={`stat-card ${card.cls}${(card.key === 'total' && !filterStatus) || filterStatus === card.key ? ' stat-card-active' : ''}`}
                        onClick={() => this.handleStatCardClick(card.key)}
                        style={{cursor: 'pointer'}}
                      >
                        <div className="stat-label">{card.label}</div>
                        <div className="stat-value">{summary[card.key] || 0}</div>
                      </div>
                    </Tooltip>
                  </div>
                ))}
              </div>

              {/* Filter Bar */}
              <div className="workflow-section">
                <div className="rounded-card">
                  <div className="workflow-filter-controls">
                    <a
                      onClick={(e) => this.setState({bulkActionsAnchor: e.currentTarget})}
                      className={selectedItems.length > 0 ? 'filter-btn active' : 'filter-btn'}
                      style={{cursor: 'pointer', position: 'static', margin: '0', marginRight: '16px'}}
                    >
                      <span className="material-icons">checklist</span>
                      Actions {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
                    </a>
                    <FormControl size="small" className="workflow-filter-field">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filterStatus}
                        onChange={(e) => this.handleStatusFilterChange(e.target.value)}
                        data-testid="cc-filter-status"
                        inputProps={{'data-testid': 'cc-filter-status-input'}}
                      >
                        <MenuItem value="">All</MenuItem>
                        {['PENDING', 'APPROVED', 'REJECTED'].map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" className="workflow-filter-field" style={{marginRight: '24px'}}>
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={filterSource}
                        onChange={(e) => this.handleFilterChange('filterSource', e.target.value)}
                        data-testid="cc-filter-source"
                        inputProps={{'data-testid': 'cc-filter-source-input'}}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="MANUAL">Manual</MenuItem>
                        <MenuItem value="REFRESH">Refresh</MenuItem>
                        <MenuItem value="MONITOR">Monitor</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" className="workflow-filter-field" style={{marginRight: '24px'}}>
                      <InputLabel>Schema</InputLabel>
                      <Select
                        value={effectiveFilterSchema}
                        onChange={(e) => this.handleSchemaFilterChange(e.target.value)}
                        data-testid="cc-filter-schema"
                        inputProps={{'data-testid': 'cc-filter-schema-input'}}
                      >
                        <MenuItem value="">All</MenuItem>
                        {schemaOptions.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <a
                      onClick={() => this.setState({filterDrawerOpen: true})}
                      className={this.state.filterFrom || this.state.filterTo ? 'filter-btn active' : 'filter-btn'}
                      style={{cursor: 'pointer', position: 'static', margin: '0'}}
                    >
                      <span className="material-icons">tune</span>
                      More Filters {(this.state.filterFrom || this.state.filterTo) ? '(1)' : ''}
                    </a>
                  </div>
                </div>
              </div>

              {/* Filter Drawer */}
              <Drawer anchor="right" open={this.state.filterDrawerOpen} onClose={() => this.setState({filterDrawerOpen: false})}>
                <div className="filter-side-sheet">
                  <div className="title">
                    <h3>Filters</h3>
                    <a className="material-icons" onClick={() => this.setState({filterDrawerOpen: false})}>close</a>
                  </div>
                  <div className="filter-content">
                    <FormControl size="small" fullWidth style={{marginBottom: '20px'}}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filterStatus}
                        onChange={(e) => this.handleStatusFilterChange(e.target.value)}
                        data-testid="cc-filter-status-drawer"
                        inputProps={{'data-testid': 'cc-filter-status-drawer-input'}}
                      >
                        <MenuItem value="">All</MenuItem>
                        {['PENDING', 'APPROVED', 'REJECTED'].map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth style={{marginBottom: '20px'}}>
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={filterSource}
                        onChange={(e) => this.handleFilterChange('filterSource', e.target.value)}
                        data-testid="cc-filter-source-drawer"
                        inputProps={{'data-testid': 'cc-filter-source-drawer-input'}}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="MANUAL">Manual</MenuItem>
                        <MenuItem value="REFRESH">Refresh</MenuItem>
                        <MenuItem value="MONITOR">Monitor</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth style={{marginBottom: '20px'}}>
                      <InputLabel>Schema</InputLabel>
                      <Select
                        value={effectiveFilterSchema}
                        onChange={(e) => this.handleSchemaFilterChange(e.target.value)}
                        data-testid="cc-filter-schema-drawer"
                        inputProps={{'data-testid': 'cc-filter-schema-drawer-input'}}
                      >
                        <MenuItem value="">All</MenuItem>
                        {schemaOptions.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="From Date"
                      type="date"
                      size="small"
                      fullWidth
                      InputLabelProps={{shrink: true}}
                      value={this.state.filterFrom}
                      onChange={(e) => this.handleFilterChange('filterFrom', e.target.value)}
                      style={{marginBottom: '20px'}}
                    />
                    <TextField
                      label="To Date"
                      type="date"
                      size="small"
                      fullWidth
                      InputLabelProps={{shrink: true}}
                      value={this.state.filterTo}
                      onChange={(e) => this.handleFilterChange('filterTo', e.target.value)}
                      style={{marginBottom: '20px'}}
                    />
                  </div>
                  <div className="cta-content">
                    <Button
                      label="Apply filters"
                      className="btn btn-action"
                      onClick={() => {
                        this.loadData(); this.setState({filterDrawerOpen: false});
                      }}
                    />
                    <Button
                      label="Clear all"
                      className="btn btn-default"
                      onClick={() => {
                        this.setState({
                          filterStatus: '', filterSource: '', filterFrom: '', filterTo: '',
                          filterInstance: '', filterSchema: '', filterDrawerOpen: false,
                        }, this.loadData);
                      }}
                    />
                  </div>
                </div>
              </Drawer>

              {/* Selection count indicator */}
              {selectedItems.length > 0 && (
                <div className="workflow-section">
                  <div className="wf-bulk-toolbar">
                    <span className="wf-bulk-count">{selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected</span>
                  </div>
                </div>
              )}

              {/* Grouped Table */}
              <div className="workflow-section">
                {isLoadingChanges ? (
                    <Preloader loading={true} />
                  ) : visibleChanges.length === 0 ? (
                    <EmptyState message={filterStatus ?
                      `No ${filterStatus.toLowerCase()} change requests match the current filters.` :
                      'No active change requests. Implemented (closed) changes are recorded in Version History below.'} />
                  ) : (
                    <Table className="data-table clickable" size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedItems.length > 0 && selectedItems.length < visibleChanges.length}
                              checked={visibleChanges.length > 0 && selectedItems.length === visibleChanges.length}
                              onChange={this.handleSelectAllItems}
                            />
                          </TableCell>
                          <TableCell><b>Object / Field</b></TableCell>
                          <TableCell><b>Schema</b></TableCell>
                          <TableCell><b>Source</b></TableCell>
                          <TableCell><b>Changes</b></TableCell>
                          <TableCell><b>Change Type</b></TableCell>
                          <TableCell><b>Status</b></TableCell>
                          <TableCell><b>Source Check</b></TableCell>
                          <TableCell><b>Requested By</b></TableCell>
                          <TableCell><b>Date</b></TableCell>
                          <TableCell align="right"><b>Actions</b></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedGroups.map(([groupKey, groupItems]) => {
                          const isExpanded = expandedGroups[groupKey];
                          const objectName = (groupItems[0] && groupItems[0].object_name) || '(unknown)';
                          const instanceName = groupItems[0] && groupItems[0].instance_name;
                          const groupStatus = this.getGroupStatus(groupItems);
                          const sortedChildren = this.sortChildRows(groupItems);
                          const groupIds = groupItems.map((i) => i.request_id);
                          const groupAllSelected = groupIds.every((id) => selectedSet.has(id));
                          const groupSomeSelected = groupIds.some((id) => selectedSet.has(id));
                          const hasPending = groupItems.some((i) => i.status === 'PENDING');
                          const changeTypes = [...new Set(groupItems.map((i) => i.change_type).filter(Boolean))];
                          const sources = [...new Set(groupItems.map((i) => i.change_source).filter(Boolean))];
                          // Groups are scoped to one Data Dictionary + schema, so this is a single schema.
                          const schemas = [...new Set(groupItems.map((i) => i.schema_name).filter(Boolean))];

                          return (
                            <React.Fragment key={groupKey}>
                              {/* Group Header Row */}
                              <TableRow className="wf-group-row" onClick={() => this.toggleGroup(groupKey)}>
                                <TableCell
                                  padding="checkbox"
                                  onClick={(e) => {
                                    e.stopPropagation(); this.toggleGroupSelection(groupItems);
                                  }}
                                >
                                  <Checkbox
                                    indeterminate={groupSomeSelected && !groupAllSelected}
                                    checked={groupAllSelected}
                                  />
                                </TableCell>
                                <TableCell>
                                  <span className="wf-expand-indicator">{isExpanded ? '▼' : '▶'}</span>
                                  <strong><a>{objectName}</a></strong>
                                  {instanceName && (
                                    <span className="wf-dd-badge" data-testid={`cc-dd-badge-${groupKey}`}>{instanceName}</span>
                                  )}
                                </TableCell>
                                <TableCell>{schemas.join(', ') || '—'}</TableCell>
                                <TableCell>
                                  <span style={{textTransform: 'capitalize'}}>
                                    {sources.join(' / ').toLowerCase() || '—'}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {groupItems.length} change{groupItems.length !== 1 ? 's' : ''}
                                </TableCell>
                                <TableCell>
                                  <span style={{textTransform: 'capitalize'}}>
                                    {changeTypes.join(', ').toLowerCase() || '—'}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={groupStatus === 'MIXED' ? 'Mixed' : groupStatus.split('_').map((word) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')}
                                    size="small"
                                    className={`status-chip ${groupStatus === 'MIXED' ? 'chip-mixed' : (STATUS_CHIP_CLASSES[groupStatus] || 'chip-grey')}`}
                                  />
                                </TableCell>
                                {/* Source Check, Requested By and Date are per-field only — a group can
                                    span users / dates / results, so object-level values would mislead. */}
                                <TableCell>—</TableCell>
                                <TableCell>—</TableCell>
                                <TableCell>—</TableCell>
                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                  {hasPending && (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.setState({groupMenuAnchor: e.currentTarget, groupMenuItems: groupItems});
                                      }}
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>

                              {/* Child Rows — shown when group is expanded */}
                              {isExpanded && sortedChildren.map((row) => (
                                <TableRow
                                  key={row.request_id}
                                  data-testid={'workflow-child-row-' + (row.column_name || row.request_id)}
                                  className={`wf-child-row ${selectedChangeRequest && selectedChangeRequest.request_id === row.request_id ? 'wf-row-selected' : ''}`}
                                  onClick={() => this.handleRowClick(row)}
                                  style={{cursor: 'pointer'}}
                                >
                                  <TableCell
                                    padding="checkbox"
                                    onClick={(e) => {
                                      e.stopPropagation(); this.toggleItemSelection(row.request_id);
                                    }}
                                  >
                                    <Checkbox checked={selectedSet.has(row.request_id)} />
                                  </TableCell>
                                  <TableCell>
                                    <a>{(() => {
                                      if (row.column_name) return row.column_name;
                                      if (row.field_changed) return row.field_changed;
                                      try {
                                        const proposed = typeof row.proposed_changes === 'object' && row.proposed_changes !== null ?
                                          row.proposed_changes :
                                          JSON.parse(row.proposed_changes || '{}');
                                        const diffs = proposed && proposed.columnDiffs;
                                        if (Array.isArray(diffs) && diffs.length > 0) {
                                          return diffs.map((d) => d.columnName).filter(Boolean).join(', ') || '—';
                                        }
                                      } catch (e) {/* ignore parse errors */}
                                      return '—';
                                    })()}</a>
                                  </TableCell>
                                  <TableCell data-testid={'cc-schema-' + (row.column_name || row.request_id)}>
                                    {row.schema_name || '—'}
                                  </TableCell>
                                  <TableCell>
                                    <span style={{textTransform: 'capitalize'}}>
                                      {(row.change_source || '—').toLowerCase()}
                                    </span>
                                  </TableCell>
                                  <TableCell onClick={(e) => e.stopPropagation()}>
                                    {this.renderChildRowChanges(row)}
                                  </TableCell>
                                  <TableCell>
                                    <span style={{textTransform: 'capitalize'}}>
                                      {(row.change_type || '—').toLowerCase()}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={row.status ? row.status.split('_').map((word) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ') : '—'}
                                      size="small"
                                      className={`status-chip ${STATUS_CHIP_CLASSES[row.status] || 'chip-grey'}`}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      data-testid={'cc-source-' + (row.column_name || row.request_id)}
                                      label={sourceCheckMeta(row).label}
                                      size="small"
                                      className={`status-chip ${sourceCheckMeta(row).cls}`}
                                    />
                                  </TableCell>
                                  <TableCell>{row.requested_by_name || row.requested_by || '—'}</TableCell>
                                  <TableCell>
                                    {row.requested_at ? new Date(row.requested_at).toLocaleDateString() : '—'}
                                  </TableCell>
                                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                    {row.status === 'REJECTED' && (
                                      <Tooltip title="Re-check the live source DB">
                                        <IconButton
                                          size="small"
                                          data-testid={'cc-act-checklive-' + (row.column_name || row.request_id)}
                                          disabled={this.state.recheckingId === row.request_id}
                                          onClick={(e) => {
                                            e.stopPropagation(); this.handleRecheckSource(row.request_id);
                                          }}
                                        >
                                          <span className="material-icons" style={{fontSize: 18}}>
                                            {this.state.recheckingId === row.request_id ? 'hourglass_empty' : 'sync'}
                                          </span>
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {(row.status === 'PENDING' || (canRollback && ['APPROVED', 'REJECTED'].includes(row.status))) && (
                                      <IconButton
                                        size="small"
                                        data-testid={'workflow-row-menu-' + (row.column_name || row.request_id)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          this.setState({rowMenuAnchor: e.currentTarget, rowMenuRow: row});
                                        }}
                                      >
                                        <MoreVertIcon />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
              </div>

              {/* Version History (chronological feed, #595) */}
              <div className="workflow-section" data-testid="cc-vh-section">
                <div className="vh-card">
                  <div className="vh-head" data-testid="cc-vh-toggle"
                    onClick={() => {
                      const next = !historyExpanded;
                      this.setState({historyExpanded: next});
                      // Reload on every expand so newly-closed changes appear without a page refresh.
                      if (next) this.props.loadHistory();
                    }}
                    style={{cursor: 'pointer'}}
                  >
                    <div className="title">
                      <span className="wf-expand-indicator">{historyExpanded ? '▼' : '▶'}</span>
                      <h3>Version History</h3>
                    </div>
                  </div>
                  {historyExpanded && (isLoadingHistory ? <Preloader loading={true} /> : this.renderVersionHistory())}
                </div>
              </div>

            </div>{/* end cz-container */}

            {/* Rollback Confirmation Dialog */}
            <Dialog open={rollbackDialogOpen} onClose={this.closeRollbackDialog} maxWidth="sm" fullWidth>
              <DialogTitle>
                <span>Confirm Rollback</span>
              </DialogTitle>
              <DialogContent dividers>
                {rollbackTargetRequest && (
                  <>
                    <div style={{
                      backgroundColor: '#fff3e0',
                      border: '1px solid #ffcc80',
                      borderRadius: 8,
                      padding: '12px 16px',
                      marginBottom: 16,
                    }}>
                      <Typography variant="body2" style={{fontWeight: 600, color: '#e65100'}}>
                        Warning: This action cannot be undone automatically.
                      </Typography>
                      <Typography variant="body2" style={{color: '#555', marginTop: 4}}>
                        This will reopen the change request to <strong>PENDING</strong> for re-review
                        (Approve / Reject){rollbackTargetRequest.status === 'APPROVED' ? ' and restore the entity to its previous state' : ''}.
                      </Typography>
                    </div>
                    <div style={{marginBottom: 16}}>
                      <Typography variant="subtitle2" color="textSecondary">Change Request</Typography>
                      <Typography variant="body1">
                        <strong>{rollbackTargetRequest.object_name || rollbackTargetRequest.request_id}</strong>
                        {rollbackTargetRequest.column_name && ` → ${rollbackTargetRequest.column_name}`}
                      </Typography>
                    </div>
                    <div style={{display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16}}>
                      <Chip
                        label={rollbackTargetRequest.status}
                        size="small"
                        className={`status-chip ${STATUS_CHIP_CLASSES[rollbackTargetRequest.status] || 'chip-grey'}`}
                      />
                      <span style={{color: '#9e9e9e'}}>{'→'}</span>
                      <Chip
                        label={'PENDING'}
                        size="small"
                        className={'status-chip chip-orange'}
                      />
                    </div>
                  </>
                )}
                <TextField
                  label="Reason for Rollback *"
                  value={rollbackReason}
                  onChange={(e) => this.setState({rollbackReason: e.target.value, rollbackReasonError: false})}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Explain why this action is being rolled back..."
                  error={rollbackReasonError}
                  helperText={rollbackReasonError ? 'A reason is required to perform a rollback.' : ''}
                  variant="outlined"
                />
              </DialogContent>
              <DialogActions>
                <Button label="Cancel" className="btn btn-default" onClick={this.closeRollbackDialog} disabled={isSubmittingAction} />
                <Button
                  label={isSubmittingAction ? 'Rolling Back...' : 'Confirm Rollback'}
                  className="btn btn-action"
                  onClick={this.confirmRollback}
                  disabled={isSubmittingAction}
                />
              </DialogActions>
            </Dialog>

            {/* Change Detail Sheet */}
            <ChangeRequestDetailSheet
              open={changeDialogOpen}
              onClose={() => this.setState({changeDialogOpen: false, selectedChangeRequest: null})}
              changeRequest={selectedChangeRequest}
              timeline={this.state.timeline}
              changes={visibleChanges}
              isLoading={this.props.isSubmittingAction}
              onNavigate={this.handleNavigateChangeRequest}
              onAction={this.handleDialogAction}
              onRollback={() => {
                const targetRequest = selectedChangeRequest;
                this.setState({changeDialogOpen: false, selectedChangeRequest: null});
                this.openRollbackDialog(targetRequest);
              }}
            />

            {/* Field Version History Sheet (#595) */}
            <FieldVersionHistorySheet
              open={this.state.fieldTimelineOpen}
              entity={this.state.fieldTimelineFor}
              timeline={this.state.fieldTimeline}
              isLoading={this.state.fieldTimelineLoading}
              onClose={() => this.setState({fieldTimelineOpen: false, fieldTimelineFor: null, fieldTimeline: []})}
            />

            <Snackbar
              open={snackbarOpen}
              message={snackbarMessage}
              autoHideDuration={4000}
              onClose={() => this.setState({snackbarOpen: false})}
            />

            {/* Group Actions Menu */}
            <Menu
              anchorEl={groupMenuAnchor}
              open={Boolean(groupMenuAnchor)}
              onClose={() => this.setState({groupMenuAnchor: null, groupMenuItems: null})}
            >
              <MenuItemMUI
                data-testid="workflow-group-approve-all"
                onClick={() => {
                  this.handleGroupAction(groupMenuItems, 'APPROVED');
                  this.setState({groupMenuAnchor: null, groupMenuItems: null});
                }}
              >
                Approve All
              </MenuItemMUI>
              <MenuItemMUI
                data-testid="workflow-group-reject-all"
                onClick={() => {
                  this.handleGroupAction(groupMenuItems, 'REJECTED');
                  this.setState({groupMenuAnchor: null, groupMenuItems: null});
                }}
              >
                Reject All
              </MenuItemMUI>
            </Menu>

            {/* Row Actions Menu */}
            <Menu
              anchorEl={rowMenuAnchor}
              open={Boolean(rowMenuAnchor)}
              onClose={() => this.setState({rowMenuAnchor: null, rowMenuRow: null})}
            >
              {rowMenuRow?.status === 'PENDING' && [
                <MenuItemMUI
                  key="approve"
                  data-testid="workflow-row-approve"
                  onClick={() => {
                    this.handleInlineAction(rowMenuRow, 'APPROVED');
                    this.setState({rowMenuAnchor: null, rowMenuRow: null});
                  }}
                >
                  Approve
                </MenuItemMUI>,
                <MenuItemMUI
                  key="reject"
                  data-testid="workflow-row-reject"
                  onClick={() => {
                    this.handleInlineAction(rowMenuRow, 'REJECTED');
                    this.setState({rowMenuAnchor: null, rowMenuRow: null});
                  }}
                >
                  Reject
                </MenuItemMUI>,
              ]}
              {rowMenuRow && canRollback && ['APPROVED', 'REJECTED'].includes(rowMenuRow.status) && (
                <MenuItemMUI
                  onClick={() => {
                    this.handleInlineAction(rowMenuRow, 'ROLLBACK');
                    this.setState({rowMenuAnchor: null, rowMenuRow: null});
                  }}
                >
                  Rollback
                </MenuItemMUI>
              )}
            </Menu>

          </div>
        </div>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = (state) => ({
  changes: state.workflowReducer.changes || [],
  changesTotal: state.workflowReducer.changesTotal,
  isLoadingChanges: state.workflowReducer.isLoadingChanges,
  isSubmittingAction: state.workflowReducer.isSubmittingAction,
  history: state.workflowReducer.history || [],
  isLoadingHistory: state.workflowReducer.isLoadingHistory,
  permissions: state.workflowReducer.permissions || [],
});

const mapDispatchToProps = (dispatch) => ({
  loadChanges: (filters) => dispatch(loadChanges(filters)),
  applyAction: (requestId, action, comments) => dispatch(applyAction(requestId, action, comments)),
  rollbackAction: (requestId, reason) => dispatch(rollbackAction(requestId, reason)),
  recheckSource: (requestId) => dispatch(recheckSource(requestId)),
  loadChangeTimeline: (requestId) => dispatch(loadChangeTimeline(requestId)),
  bulkAction: (requestIds, action, comments) => dispatch(bulkAction(requestIds, action, comments)),
  loadHistory: (filters) => dispatch(loadHistory(filters)),
  loadFieldTimeline: (s, o, c, id) => dispatch(loadFieldTimeline(s, o, c, id)),
  loadPermissions: () => dispatch(loadPermissions()),
  downloadAuditReport: (filters) => dispatch(downloadAuditReport(filters)),
  downloadDetailedReport: (filters) => dispatch(downloadDetailedReport(filters)),
  downloadSummaryReport: (filters) => dispatch(downloadSummaryReport(filters)),
  loadChangeRequestById: (requestId) => dispatch(loadChangeRequestById(requestId)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(WorkflowDashboard));
